import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useCoreApi } from 'core/Api';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import {
  onboardingCauseAtom,
  onboardingReferralAtom,
  onboardingReferralCauseAtom,
  resetOnboardingAtoms,
} from '../../atoms/onboarding';
import { IReferral } from '../../pages/Referrals/ReferralLayout';

interface ReferralInterface {
  referral: IReferral;
}

function useGetUserReferral(uuid: string | undefined) {
  const coreApi = useCoreApi();
  const [, setReferralCause] = useAtom(onboardingReferralCauseAtom);
  const getReferral = async () => {
    const { data } = await coreApi.get<ReferralInterface>(`/referrals/${uuid}`);
    setReferralCause(data.referral?.cause?.uuid);
    return data.referral;
  };
  return useQuery({
    queryKey: ['referral', uuid],
    queryFn: getReferral,
    enabled: !!uuid
  });
}

const useOnboardingReferrals = () => {
  const clearOnboardingCodes = useSetAtom(resetOnboardingAtoms);
  const causeReferralCode = useAtomValue(onboardingCauseAtom);
  const referralCode = useAtomValue(onboardingReferralAtom);
  const referralCauseCode = useAtomValue(onboardingReferralCauseAtom);

  const referredCauseId = causeReferralCode ?? referralCauseCode;

  const isOnboardingLoaded = useMemo(() => {
    if (referralCode === undefined) return false;
    if (causeReferralCode === undefined) return false;
    if (referralCauseCode === undefined) return false;
    return true;
  }, [referralCode, causeReferralCode, referralCauseCode]);

  return {
    clearOnboardingCodes,
    referralCode,
    causeReferralCode,
    referredCauseId,
    isOnboardingLoaded,
    setOnboardingCause: useSetAtom(onboardingCauseAtom),
    setOnboardingReferral: useSetAtom(onboardingReferralAtom),
    useGetUserReferral,
  };
};

export { useOnboardingReferrals };
